import { formatLocalDate } from '../../../../utils/formatLocalDate';
import { fetchTargetPlanSummaries } from '../api/dietApi';
import { flattenTargetPlanSummaries } from '../diet-plan/flattenTargetPlanSummaries';

const DEFAULT_WINDOW_DAYS = 90;

/** Normalize API date strings to YYYY-MM-DD for inputs and comparisons. */
export function displayDate(value) {
  if (!value) return '';
  const str = String(value);
  return str.includes('T') ? str.split('T')[0] : str;
}

/** Human-readable plan date for UI labels (en-IN). */
export function formatScheduleDate(value) {
  if (!value) return '—';
  const dateStr = displayDate(value);
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return dateStr;
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function wideDateRange(windowDays = DEFAULT_WINDOW_DAYS) {
  const today = new Date();
  const start = new Date(today);
  start.setDate(start.getDate() - windowDays);
  const end = new Date(today);
  end.setDate(end.getDate() + windowDays);
  return { start: formatLocalDate(start), end: formatLocalDate(end) };
}

export function durationOverlapsRange(duration, startDate, endDate) {
  if (!duration || !startDate || !endDate) return false;
  const dStart = displayDate(duration.start_date);
  const dEnd = displayDate(duration.end_date);
  return dStart <= endDate && dEnd >= startDate;
}

export async function fetchPlanSummariesInWindow(
  eventTypeId,
  clientId,
  windowDays = DEFAULT_WINDOW_DAYS
) {
  if (!eventTypeId || !clientId) return [];
  // Prefer a bounded window when callers pass days; null window = all plans for client
  // (same as the user Planned table — avoids missing plans outside ±N days).
  let start = null;
  let end = null;
  if (windowDays != null && windowDays > 0) {
    const range = wideDateRange(windowDays);
    start = range.start;
    end = range.end;
  }
  const result = await fetchTargetPlanSummaries(eventTypeId, start, end, clientId);
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to fetch existing plans');
  }
  return result.data?.data?.target_plans || [];
}

export function buildExistingPlansContext(targetPlans = []) {
  const list = flattenTargetPlanSummaries(targetPlans).map((row) => ({
    id: row.id,
    duration_id: row.duration_id,
    title: row.title || 'Unnamed plan',
    start_date: displayDate(row.start_date),
    end_date: displayDate(row.end_date),
  }));
  return list.sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''));
}

export function hasPlansOverlappingRange(targetPlans, startDate, endDate) {
  if (!startDate || !endDate || startDate > endDate) return false;
  return flattenTargetPlanSummaries(targetPlans).some((row) =>
    durationOverlapsRange(row, startDate, endDate)
  );
}
